import { type AggRequest, type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../utils/caching/in-memory';

import {
	confluenceDcContentFilterOptionsOperationName,
	confluenceDcContentFilterOptionsQueryGenerator,
	type ConfluenceDcContentFilterOptionsQueryVariables,
	confluenceDcPagesOperationName,
	confluenceDcPagesQueryGenerator,
	type ConfluenceDcPagesQueryVariables,
	confluenceDcSpaceFilterOptionsOperationName,
	dcSpaceFilterOptionsQueryGenerator,
	type DcSpaceFilterOptionsQueryVariables,
	jiraDcProjectFilterOptionsOperationName,
	queryGenerator,
	resultsCountOperationName,
	resultsCountQueryGenerator,
	type ResultsCountQueryVariables,
	SEARCH_DIALOG_OPERATION_NAME,
	SEARCH_PAGE_OPERATION_NAME,
	searchLinkedSiteQueryGenerator,
	searchLinkedSitesOperationName,
	type SearchLinkedSitesQueryVariables,
	type SearchPageQueryVariables,
	spaceARIEntitiesOperationName,
	spaceARIEntitiesQueryGenerator,
	type SpaceARIEntitiesQueryVariables,
} from './query-generator';
import {
	type ConfluenceDCFilterOptionsData,
	type ConfluenceDCPagesData,
	type DCSpaceFilterOptionsData,
	type SearchLinkedSitesData,
	type SearchPageData,
	type SpaceARIEntitiesData,
} from './types';

type BaseQueryProps<T> = Pick<AggRequest<T>, 'aggAbsoluteUrl' | 'variables' | 'headers'> & {
	originalQuery?: string;
};

export type SearchPageQueryProps = BaseQueryProps<SearchPageQueryVariables>;
export type ResultsCountQueryProps = BaseQueryProps<ResultsCountQueryVariables>;
export type DcSpaceFilterOptionsQueryProps = BaseQueryProps<DcSpaceFilterOptionsQueryVariables>;
export type SpaceARIEntitiesQueryProps = BaseQueryProps<{ ids: string[] }>;
export type SearchLinkedSitesQueryProps = BaseQueryProps<{ id: string }>;
export type ConfluenceDcContentFilterOptionsQueryProps =
	BaseQueryProps<ConfluenceDcContentFilterOptionsQueryVariables>;
export type ConfluenceDcPagesQueryProps = BaseQueryProps<ConfluenceDcPagesQueryVariables>;
type SearchResultsQueryProps = SearchPageQueryProps & {
	useCache: boolean;
	operationName: string;
	serveUserShardUI?: boolean;
};

export const cache = inMemoryCache<AggResponse<SearchPageData>>();

export const generateCacheKey = (
	variables: SearchPageQueryVariables | ResultsCountQueryVariables,
	originalQuery?: string,
) => {
	/**
	 * Results once fetched will be used for upto 15 minutes from an in memory cache.
	 * 1. Generate key based on query, entities, filters
	 * 2. Serve from cache if present without resetting the expiration, evict if results are expired
	 * 3. If not present or expired then invoke the api
	 * 4. If API is invoked then store response in the cache with expiration and return back the response
	 */
	const keyObj = {
		query: variables.query,
		entities: variables.entities,
		commonFilters: variables.commonFilters,
		confluenceFilters: variables.confluenceFilters,
		jiraFilters: variables.jiraFilters,
		mercuryFilters: variables.mercuryFilters,
		compassFilters: variables.compassFilters,
		assetsFilters: variables.assetsFilters,
		thirdPartyFilters: variables.thirdPartyFilters,
		cloudIdARI: variables.cloudIdARI,
		sortField: variables.sort.at(0)?.field ?? '',
		sortOrder: variables.sort.at(0)?.order ?? '',
		after: variables.after,
		originalQuery,
	};

	return generateHashFromVariables(keyObj);
};

// Expire the cache if there are errors or edges are empty
const expireWhen = (result: AggResponse<SearchPageData>) => {
	const { data, errors } = result;
	if (!data || !data.search || (errors && errors.length > 0)) {
		return true;
	}
	return data.search?.results.edges.length === 0;
};

const searchQuery = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	useCache,
	operationName,
	serveUserShardUI = false,
	headers,
}: SearchResultsQueryProps) => {
	// Default experience is search-page
	variables.experience = variables.experience || 'search-page';

	// Default skip3pEntityHydration is false
	variables.skip3pEntityHydration =
		variables.skip3pEntityHydration || variables.skipFieldsFor3pInQuickFind || false;

	variables.includeSpaceSubtype = variables.includeSpaceSubtype || false;

	variables.skipFieldsFor3pInQuickFind = variables.skipFieldsFor3pInQuickFind || false;

	// Since the default experience is search-page, default experience is to include social signals
	variables.includeSocialSignals =
		variables.includeSocialSignals === undefined ? true : variables.includeSocialSignals;

	variables.includeJiraIssueType = variables.includeJiraIssueType || false;

	variables.includeJiraBoardContainer = variables.includeJiraBoardContainer || false; // Used to include the container field

	// Option to hydrate the confluenceEntity field
	variables.includeConfluenceEntity =
		typeof variables.includeConfluenceEntity === 'boolean'
			? variables.includeConfluenceEntity
			: true;

	const graphQuery = queryGenerator(operationName, serveUserShardUI);

	if (useCache) {
		return cache.inMemoryDecorator(
			generateCacheKey(variables, originalQuery),
			() =>
				fetchAgg<SearchPageQueryVariables, SearchPageData>({
					variables,
					aggAbsoluteUrl,
					graphQuery,
					operationName,
					headers,
				}),
			expireWhen,
		);
	} else {
		return fetchAgg<SearchPageQueryVariables, SearchPageData>({
			variables,
			aggAbsoluteUrl,
			graphQuery,
			operationName,
			headers,
		});
	}
};

export const searchPageQuery = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	serveUserShardUI = false,
}: SearchPageQueryProps & { serveUserShardUI?: boolean }) => {
	return searchQuery({
		variables,
		aggAbsoluteUrl,
		originalQuery,
		useCache: true,
		operationName: SEARCH_PAGE_OPERATION_NAME,
		serveUserShardUI,
	});
};

export const searchDialogQuery = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	serveUserShardUI = false,
	headers,
}: SearchPageQueryProps & { serveUserShardUI?: boolean }) => {
	return searchQuery({
		variables,
		aggAbsoluteUrl,
		originalQuery,
		useCache: false, // Bypassing cache to match previous Quick Find behavior
		operationName: SEARCH_DIALOG_OPERATION_NAME,
		serveUserShardUI,
		headers,
	});
};

export const resultsCountQuery = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
}: ResultsCountQueryProps) => {
	const graphQuery = resultsCountQueryGenerator();

	return cache.inMemoryDecorator('count' + generateCacheKey(variables, originalQuery), () =>
		fetchAgg<ResultsCountQueryVariables, SearchPageData>({
			variables,
			aggAbsoluteUrl,
			graphQuery,
			operationName: resultsCountOperationName,
		}),
	);
};

export const jiraDcProjectFilterOptionsQuery = ({
	variables,
	aggAbsoluteUrl,
}: DcSpaceFilterOptionsQueryProps) => {
	const graphQuery = dcSpaceFilterOptionsQueryGenerator(jiraDcProjectFilterOptionsOperationName);

	return fetchAgg<DcSpaceFilterOptionsQueryVariables, DCSpaceFilterOptionsData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: jiraDcProjectFilterOptionsOperationName,
	});
};

export const spaceARIEntitiesQuery = ({
	variables,
	aggAbsoluteUrl,
}: SpaceARIEntitiesQueryProps) => {
	const graphQuery = spaceARIEntitiesQueryGenerator();

	return fetchAgg<SpaceARIEntitiesQueryVariables, SpaceARIEntitiesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: spaceARIEntitiesOperationName,
	});
};

export const confluenceDcContentFilterOptionsQuery = ({
	variables,
	aggAbsoluteUrl,
}: ConfluenceDcContentFilterOptionsQueryProps) => {
	const graphQuery = confluenceDcContentFilterOptionsQueryGenerator();

	return fetchAgg<ConfluenceDcContentFilterOptionsQueryVariables, ConfluenceDCFilterOptionsData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: confluenceDcContentFilterOptionsOperationName,
	});
};

export const confluenceDcPagesQuery = ({
	variables,
	aggAbsoluteUrl,
}: ConfluenceDcPagesQueryProps) => {
	const graphQuery = confluenceDcPagesQueryGenerator();

	return fetchAgg<ConfluenceDcPagesQueryVariables, ConfluenceDCPagesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: confluenceDcPagesOperationName,
	});
};

export const confluenceDcSpaceFilterOptionsQuery = ({
	variables,
	aggAbsoluteUrl,
}: DcSpaceFilterOptionsQueryProps) => {
	const graphQuery = dcSpaceFilterOptionsQueryGenerator(
		confluenceDcSpaceFilterOptionsOperationName,
	);

	return fetchAgg<DcSpaceFilterOptionsQueryVariables, DCSpaceFilterOptionsData>({
		variables: {
			...variables,
			integration: 'ari:cloud:platform::integration/confluence-dc',
		},
		aggAbsoluteUrl,
		graphQuery,
		operationName: confluenceDcSpaceFilterOptionsOperationName,
	});
};

export const searchLinkedSitesQuery = ({
	variables,
	aggAbsoluteUrl,
}: SearchLinkedSitesQueryProps) => {
	const graphQuery = searchLinkedSiteQueryGenerator();

	return fetchAgg<SearchLinkedSitesQueryVariables, SearchLinkedSitesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: searchLinkedSitesOperationName,
	});
};
